"use client"

import { ReviewsSection } from "@/components/organisms/Product/review"
import { useReviewData } from "@/hooks/useReviewData"
import { SimilarProductsSectionSkeleton } from "@/components/templates/Products/similar-products"
import { useSession } from "@/contexts/SessionContext"
import { ClientProviders } from "@/app/providers"

const ReviewsWrapper = ({
  productId,
  productHandle,
  type,
  label,
  translations,
  className,
  ownerId,
}: {
  productId: string
  productHandle: string
  type: "product" | "consultant"
  label?: string
  translations: Record<string, any>
  className?: string
  ownerId?: string
}) => {
  const { session } = useSession()
  const currentUserId = session?.user?.id
  const { data, isLoading, error }: any = useReviewData(productId, type)

  const reviews = Array.isArray(data?.data?.reviews) ? data.data.reviews : []
  const averageRating = data?.data?.averageRating ?? 0
  const total = reviews.length
  const summary = type === "product" ? data?.data?.aiSummary : data?.data?.aiSummary?.summary

  // Domain checks
  const isOwner = !!(currentUserId && ownerId && currentUserId === ownerId)
  const hasReviewed = !!(currentUserId && reviews.some((r: any) => r.userId === currentUserId))
  const isLoggedIn = !!currentUserId

  const formattedReviews = reviews.map((review: any) => ({
    author: type === "product" ? review.fullName : review.userName ?? "Anonymous",
    body: review.review,
    rating: review.rating,
    createdAt: review.createdAt,
  }))

  if (isLoading) return <SimilarProductsSectionSkeleton />
  if (error) {
    console.error("❌ Review Fetch Error:", error)
    return <p className="text-sm text-red-500">Failed to load reviews.</p>
  }

  return (
    <ReviewsSection
      productId={productId}
      productHandle={productHandle}
      reviews={formattedReviews}
      total={total}
      avgRating={averageRating}
      summary={summary}
      label={label}
      type={type}
      className={type === "consultant" ? "!bg-white dark:!bg-gray-800" : className}
      translations={translations}
      isOwner={isOwner}
      hasReviewed={hasReviewed}
      isLoggedIn={isLoggedIn}
    />
  )
}

/**
 * ReviewsDynamicClient needs a SessionProvider to access useSession().
 * 
 * On product detail pages, BuyerLayout does NOT wrap children in ClientProviders.
 * Only the header (via SessionWrapper) has its own ClientProviders.
 * So the review section must create its own — but we must NOT hardcode session={null}.
 * 
 * By omitting the session prop, the SessionProvider's initialData will be undefined,
 * causing React Query to immediately fetch /api/auth/me with credentials.
 */
export const ReviewsDynamicClient = ({
  productId,
  productHandle,
  type,
  label,
  translations,
  className,
  ownerId,
}: {
  productId: string
  productHandle: string
  type: "product" | "consultant"
  label?: string
  translations: Record<string, any>
  className?: string
  ownerId?: string
}) => (
  <ClientProviders>
    <ReviewsWrapper
      productId={productId}
      productHandle={productHandle}
      type={type}
      label={label}
      translations={translations}
      className={className}
      ownerId={ownerId}
    />
  </ClientProviders>
)
